import { _ } from "../../../../core/polyglot/polyglot.js";
import { range } from "@foretagsplatsen/core-helpers/array";
import AbstractPeriodColumnTemplate from "./AbstractPeriodColumnTemplate.js";
import GroupColumn from "../../reports/columns/GroupColumn.js";
import ManualGroupColumnTemplate from "./groups/ManualGroupColumnTemplate.js";
import RelativePeriodTemplate from "../RelativePeriodTemplate.js";
import TemplateInterpreter from "../../TemplateInterpreter.js";

/**
 * A RepeatingColumnTemplate repeatedly generates a column over a specified
 * number of past or future periods, with a customizable period type.
 *
 * This template is interpreted as a {@link GroupColumn}.
 *
 * @param {AbstractColumnTemplate} columnTemplate - Column to repeat
 * @param {number} [numberOfPeriods] - Total number of resulting columns
 * @param {number} [step] - The distance between a period and the next period.
 *   Default is 1. When providing a positive value would generate forward, i.e.
 *   in the future regarding the starting period, while a negative value, the
 *   generated will go backward, i.e. in the past regarding the starting period.
 *   For example, consider the starting period Sept 2023 and numberOfPeriods
 *   = 2, period type is month.
 *	 When step is equal -1, it would generate column templates for Jul 2023,
 *	 Aug 2023, and Sept 2023.
 *	 When step is equal 2, it would generate column templates for Sept 2023,
 *	 Nov 2023, and Jan 2024.
 */
export default class RepeatingColumnTemplate extends AbstractPeriodColumnTemplate {
	constructor({ columnTemplate, numberOfPeriods = 12, step = 1 } = {}) {
		super(...arguments);

		this._columnTemplate = columnTemplate;
		this._numberOfPeriods = numberOfPeriods;
		this._step = step;
	}

	findColumnRecursively({ predicate }) {
		if (predicate(this._columnTemplate)) {
			return this._columnTemplate;
		}

		return this._columnTemplate?.findColumnRecursively({
			predicate,
		});
	}

	getStep() {
		return this._step;
	}

	setReportTemplate(...args) {
		super.setReportTemplate(...args);

		this._columnTemplate?.setReportTemplate?.(...args);
	}

	setColumnTemplate(template) {
		this._columnTemplate = template;
	}

	getColumnTemplate() {
		return this._columnTemplate;
	}

	isRepeatingColumnTemplate() {
		return true;
	}

	getNumberOfPeriods() {
		return this._numberOfPeriods;
	}

	setNumberOfPeriods(numberOfPeriods) {
		this._numberOfPeriods = numberOfPeriods;
	}

	getPeriodType() {
		return this._periodTemplate.getPeriodType();
	}

	setPeriodType(periodType) {
		this._periodTemplate.setPeriodType(periodType);
	}

	accept(visitor) {
		return visitor.visitRepeatingColumnTemplate(this);
	}

	_instanceClass() {
		return GroupColumn;
	}

	_interpretationSpec(interpreter) {
		const spec = super._interpretationSpec(...arguments);
		const shiftedInterpreter = this._cloneInterpreterForPeriod({
			interpreter,
			period: spec.period,
		});

		spec.columns = this._interpretColumns(shiftedInterpreter);

		return spec;
	}

	_interpretColumns(interpreter) {
		const columns = [];

		const indices = this._makeIndexesForColumnsInterpretation();

		indices.forEach((index) =>
			columns.push(
				this._makePeriodGroupColumn({
					index,
					interpreter,
				}),
			),
		);

		return columns;
	}

	_makeIndexesForColumnsInterpretation() {
		return this._step > 0
			? range(0, this._numberOfPeriods, this._step)
			: range(-this._numberOfPeriods + 1, 1, -this._step);
	}

	/**
	 * For each period, we create a manual group with the period title,
	 * and interpret the sub-column template in the context of this period.
	 */
	_makePeriodGroupColumn({ index, interpreter }) {
		const period = this._shiftPeriod({ index, interpreter });
		const shiftedInterpreter = this._cloneInterpreterForPeriod({
			period,
			interpreter,
		});

		return shiftedInterpreter.interpretItemTemplate(
			new ManualGroupColumnTemplate({
				title: period.toSmartString(),
				columnTemplates: [this._columnTemplate],
			}),
		);
	}

	_cloneInterpreterForPeriod({ interpreter, period }) {
		const context = interpreter.getContext();
		const newContext = context.clone({ period });

		return new TemplateInterpreter({
			context: newContext,
		});
	}

	_shiftPeriod({ index, interpreter }) {
		const periodTemplate = new RelativePeriodTemplate({
			periodType: this.getPeriodType(),
			shiftPeriodType: this.getPeriodType(),
			shift: index,
		});

		return periodTemplate.getPeriodForContext(interpreter.getContext());
	}

	static getLabel() {
		return _("Repeating Column");
	}

	static getType() {
		return "RepeatingColumnTemplate";
	}
}
